import { useMutation } from '@apollo/client';
import { asyncTimeout, useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  JoinShelterSendContactMessageMutation,
  JoinShelterSendContactMessageMutationVariables,
} from '@gql/graphql';
import { ContactReasonTypeEnum } from '@gql/graphql';
import type { JoinShelterPageFormValues } from '@presentation/pages';
import { useCallback } from 'react';

export const JOIN_SHELTER_SEND_CONTACT_MESSAGE = graphql(`
  mutation JoinShelterSendContactMessage(
    $email: String!
    $language: String!
    $phone: String
    $lastName: String
    $firstName: String
    $contactReasonType: ContactReasonTypeEnum!
  ) {
    contactSupport(
      email: $email
      language: $language
      phone: $phone
      lastName: $lastName
      firstName: $firstName
      contactReasonType: $contactReasonType
    )
  }
`);

export const useSendContactMessage = () => {
  const { language, translate, navigateToRoute } = useRouter();
  const { showToast } = useShowToast();

  const [send, { loading, error }] = useMutation<
    JoinShelterSendContactMessageMutation,
    JoinShelterSendContactMessageMutationVariables
  >(JOIN_SHELTER_SEND_CONTACT_MESSAGE);

  const showSuccessToast = useCallback(() => {
    showToast(translate('common.toasts.emailSent'), { type: 'success', autoClose: 1500 });
  }, [showToast, translate]);

  const sendContactMessageFromForm = useCallback(
    async (values: JoinShelterPageFormValues) => {
      const { email, firstName, lastName, phone } = values;

      await send({
        variables: {
          email,
          firstName,
          lastName,
          phone,
          language,
          contactReasonType: ContactReasonTypeEnum.SHELTER_REGISTRATION_REQUEST,
        },
      });

      showSuccessToast();
      await asyncTimeout(() => {
        navigateToRoute('homeLanding');
      }, 2000);
    },
    [language, navigateToRoute, send, showSuccessToast],
  );

  return {
    sendContactMessageFromForm,
    sendContactMessageLoading: loading,
    sendContactMessageError: error,
  };
};
